import React, { useEffect, useState } from "react";

import GlobalContext, { defaultDetails, defaultFormValues } from "./context";
import axios from "axios";
import { Alert, Snackbar } from "@mui/material";
import { withTranslation } from "react-i18next";
import i18n from '../i18n/i18n';
import { isInCart } from "../utils/cart";

// Debug eszköz betöltése fejlesztési környezetben
if (process.env.NODE_ENV === 'development') {
    import('../utils/localStorage-debug.js');
}

// localStorage validáció és tisztítás
const LOCALSTORAGE_VERSION = '1.0';

const validateLocalStorageData = () => {
    try {
        const currentVersion = window.localStorage.getItem('localStorageVersion');
        
        // Ha nincs verzió vagy régebbi verzió, reseteljük a localStorage-t
        if (currentVersion !== LOCALSTORAGE_VERSION) {
            console.warn('Régi localStorage formátum észlelve, törlés...');
            clearInvalidLocalStorage();
            window.localStorage.setItem('localStorageVersion', LOCALSTORAGE_VERSION);
            return false;
        }

        // Ellenőrizzük a kritikus adatok validitását
        const cart = window.localStorage.getItem('cart');
        if (cart) {
            const parsedCart = JSON.parse(cart);
            // Ellenőrizzük hogy a cart objektum rendelkezik az alapvető tulajdonságokkal
            if (!parsedCart || typeof parsedCart !== 'object' || 
                (parsedCart.id && typeof parsedCart.id !== 'number')) {
                console.warn('Érvénytelen cart adat észlelve nyugat-Storage-ban, törlés...');
                clearInvalidLocalStorage();
                return false;
            }
        }

        // Ellenőrizzük a formValues validitását
        const formValues = window.localStorage.getItem('formValues');
        if (formValues) {
            const parsedFormValues = JSON.parse(formValues);
            if (!parsedFormValues || typeof parsedFormValues !== 'object') {
                console.warn('Érvénytelen formValues adat észlelve localStorage-ban, törlés...');
                clearInvalidLocalStorage();
                return false;
            }
        }

        return true;
    } catch (error) {
        console.error('Hiba a localStorage validáció során:', error);
        clearInvalidLocalStorage();
        return false;
    }
};

const clearInvalidLocalStorage = () => {
    // Csak a kosárhoz és foglaláshoz kapcsolódó adatokat törölöük
    const keysToRemove = [
        'formValues', 'details', 'responseId', 'orderId', 'cart', 
        'cartSum', 'transfer', 'lounge', 'parking', 'fastTrack'
    ];
    
    keysToRemove.forEach(key => {
        window.localStorage.removeItem(key);
    });
    
    console.info('Érvénytelen localStorage adatok törölve');
};

// Biztonságos JSON parse funkció
const safeParseJSON = (item, defaultValue = null) => {
    try {
        const value = window.localStorage.getItem(item);
        return value ? JSON.parse(value) : defaultValue;
    } catch (error) {
        console.warn(`Hiba a ${item} parse-olása során:`, error);
        window.localStorage.removeItem(item);
        return defaultValue;
    }
};

// Biztonságos GET operation localStorage-ból integer értékekhez
const safeParseInt = (key, defaultValue = 0) => {
    try {
        const value = window.localStorage.getItem(key);
        return value ? parseInt(value, 10) : defaultValue;
    } catch (error) {
        console.warn(`Hiba a ${key} parseInt során:`, error);
        window.localStorage.removeItem(key);
        return defaultValue;
    }
};

const defaultContextData = {
    formValues: defaultFormValues(),
    details: defaultDetails(),
    responseId: safeParseInt('responseId'),
    orderId: safeParseInt('orderId'),
    cart: safeParseJSON('cart'),
    cartSum: safeParseJSON('cartSum'),
    transfer: safeParseJSON('transfer'),
    lounge: safeParseJSON('lounge'),
    parking: safeParseJSON('parking'),
    fastTrack: safeParseJSON('fastTrack'), // javítva 'parfastTrackking' helyett
    user: null,
    currency: window.localStorage.getItem('currency') || 'EUR',
    authKey: window.localStorage.getItem('authKey') || null,
    activationSuccess: false,
    activationFailure: false,
};

// localStorage validáció futtatása az alkalmazás betöltésekor
validateLocalStorageData();

const defaultSnack = {
    message: '',
    open: false,
    severity: 'success'
}

const getLocalString = (datet) => {

    return new Date(datet).toISOString().substring(0, 10) + "T" +  new Date(datet).toLocaleTimeString();

}

const GlobalState = props => {

    // Biztonságos GET operation localStorage-ból integer értékekhez
    const safeParseInt = (key, defaultValue = 0) => {
        try {
            const value = window.localStorage.getItem(key);
            return value ? parseInt(value, 10) : defaultValue;
        } catch (error) {
            console.warn(`Hiba a ${key} parseInt során:`, error);
            window.localStorage.removeItem(key);
            return defaultValue;
        }
    };

    // localStorage validáció futtatása az alkalmazás betöltésekor
    validateLocalStorageData();

    const [contextData, setContextData] = useState(defaultContextData);
    const [snack, setSnack] = useState(defaultSnack);

    // Kosár érvényességi ellenőrzés
    const validateCart = (cart) => {
        if (!cart || !cart.id) return false;
        
        // Ellenőrizzük hogy a kosár nem túl régi-e (24 óránál régebbi)
        if (cart.createdDate) {
            const cartDate = new Date(cart.createdDate);
            const now = new Date();
            const hoursDiff = (now - cartDate) / (1000 * 60 * 60);
            
            if (hoursDiff > 24) {
                console.warn('Lejárt kosár észlelve, törlés szükséges');
                return false;
            }
        }
        
        return true;
    };

    // Kosár validáció futtatása ha van kosár adat
    useEffect(() => {
        if (contextData.cart && !validateCart(contextData.cart)) {
            console.warn('Érvénytelen kosár észlelve, resetelés...');
            resetCart();
        }
    }, []); // Csak egyszer fusson le az alkalmazás betöltéskor

    useEffect(() => {
        if (contextData?.authKey && !contextData?.user) {
            axios.get('/api/account', { headers: { Authorization: `Bearer ${contextData?.authKey}` } })
                .then((resp) => {
                    if (resp?.data?.id) {
                        setContextData((contextData) => ({ ...contextData, user: resp?.data }))
                    }
                }).catch(err => console.log(err));
        }
    }, [contextData?.authKey])

    useEffect(() => {
        if (contextData?.cart?.id) {
            axios.get(`/api/custom/price-calculation/prices/${contextData?.cart?.id}?currency=${contextData?.currency}`, { headers: { Authorization: `Bearer ${contextData?.authKey}` } })
                .then((resp) => {
                    if (resp?.data) {
                        setContextData((contextData) => ({ ...contextData, cartSum: resp?.data }))
                    }
                }).catch(err => console.log(err));
        }
    }, [contextData?.cart])

    const login = (username, pass) => {
        axios.post('/api/authenticate', { username: username, password: pass })
            .then((resp) => {
                console.log(resp, resp?.data?.id_token)
                // window.localStorage.setItem('user');
                if (resp?.data?.id_token) {
                    setSnack({
                        message: i18n.t('messages.loginSuccess'),
                        severity: 'success',
                        open: true
                    })
                    window.localStorage.setItem('authKey', resp?.data?.id_token);
                    setContextData((contextData) => ({ ...contextData, authKey: resp?.data?.id_token }))

                }

            }).catch(err => {
                console.log(err)
                setSnack({
                    message: i18n.t('messages.loginError'),
                    severity: 'error',
                    open: true
                })
            });
    }

    const resetPassword = (username) => {
        axios.post('/api/account/reset-password/init', username, { headers: { 'Content-Type': 'text/plain' } })
            .then((resp) => {
                console.log(resp, resp?.data?.id_token)
                // window.localStorage.setItem('user');
                    setSnack({
                        message: i18n.t('messages.checkEmail'),
                        severity: 'success',
                        open: true
                    })

            }).catch(err => {
                console.log(err)
            });
    }

    const resetPasswordFinish = (key, newPassword) => {
        axios.post('/api/account/reset-password/finish', { key, newPassword })
            .then((resp) => {
                setSnack({
                    message: i18n.t('messages.passwordResetSuccess'),
                    severity: 'success',
                    open: true
                })
            }).catch(err => {
                setSnack({
                    message: i18n.t('messages.passwordResetError'),
                    severity: 'error',
                    open: true
                })
            });
    }

    const register = (values) => {
        axios.post('/api/register', {
            login: values.username,
            email: values.username,
            password: values.pass,
            firstName: values.firstName,
            lastName: values.lastName,
        })
            .then((resp) => {
                // login(values.username, values.pass);
                setSnack({
                    message: i18n.t('messages.registrationSuccess'),
                    severity: 'success',
                    open: true
                })
            }).catch(err => {
                console.log(err)
                setSnack({
                    message: i18n.t('messages.registrationError'),
                    severity: 'error',
                    open: true
                })
            });
    }

    const logout = () => {
        window.localStorage.removeItem('user');
        window.localStorage.removeItem('authKey');
        setContextData((contextData) => ({ ...contextData, user: null, authKey: null }))
    }

    const deleteMyProfile = () => {

        if (contextData?.user?.login) {
            axios.delete(`/api/custom/users`, {
                headers: {
                    Authorization: `Bearer ${contextData?.authKey}`
                }
            })
                .then(() => {
                    logout();
                })
                .catch(err => console.log('err', err));
        }


    }

    const activate = (key) => {
        axios.get(`/api/activate?key=${key}`)
            .then(() => {
                setContextData((contextData) => ({ ...contextData, activationSuccess: true, activationFailure: false }));
            })
            .catch(() => {
                setContextData((contextData) => ({ ...contextData, activationSuccess: false, activationFailure: true }));
            });
    }

    const resetActivate = () => {
        setContextData((contextData) => ({ ...contextData, activationSuccess: false, activationFailure: false }));
    }

    const isLoggedIn = () => {
        return contextData?.user?.id && contextData?.authKey;
    }

    const removeFromCart = (p, prefix) => {
        let suffix = '';
        let idVariable = '';

        const cartInfo = isInCart(contextData.cart, p);
        
        if (!cartInfo.isInCart || !contextData.cart?.id) return;

        switch(prefix) {
            case 'parkings':
                suffix = 'delete-parking';
                idVariable = 'parkingId';
                break;
            case 'fast-tracks':
                suffix = 'delete-fast-track';
                idVariable = 'fastTrackId';
                break;
            case 'lounges':
                suffix = 'delete-lounge';
                idVariable = 'loungeId';
                break;
            case 'transfers':
                suffix = 'delete-transfer';
                idVariable = 'transferId';
                break;
        }

        let idValue = null;
        switch(cartInfo.direction) {
            case 'directionFrom-departureTransfer':
                idValue = contextData.cart?.directionFrom?.departureTransfer?.id;
                break;
            case 'directionTo-departureTransfer':
                idValue = contextData.cart?.directionTo?.departureTransfer?.id;
                break;
            case 'directionFrom-arrivalTransfer':
                idValue = contextData.cart?.directionFrom?.arrivalTransfer?.id;
                break;
            case 'directionTo-arrivalTransfer':
                idValue = contextData.cart?.directionTo?.arrivalTransfer?.id;
                break;
            case 'directionFrom-departureParking':
                idValue = contextData.cart?.directionFrom?.departureParking?.id;
                break;
            case 'directionTo-departureParking':
                idValue = contextData.cart?.directionTo?.departureParking?.id;
                break;
            case 'directionFrom-departureLounge':
                idValue = contextData.cart?.directionFrom?.departureLounge?.id;
                break;
            case 'directionTo-departureLounge':
                idValue = contextData.cart?.directionTo?.departureLounge?.id;
                break;
            case 'directionFrom-departureFastTrack':
                idValue = contextData.cart?.directionFrom?.departureFastTrack?.id;
                break;
            case 'directionTo-departureFastTrack':
                idValue = contextData.cart?.directionTo?.departureFastTrack?.id;
                break;
        }

        axios.put(`/api/custom/shopping-carts/${contextData.cart.id}/${suffix}?${idVariable}=${idValue}&currency=${contextData.currency}`)
            .then(res => {
                if (res?.data) {
                    setSnack({
                        message: i18n.t('cart.removedFromCart'),
                        severity: 'success',
                        open: true
                    });
                    setCart(res?.data);
                }
            }).catch(err => {
                setSnack({
                    message: i18n.t('cart.removeFromCartError'),
                    severity: 'error',
                    open: true
                });
                console.log(err);
            });
    };

    const addToCart = (p, prefix) => {

        if (contextData.cart && contextData.cart.id) {
            const cartInfo = isInCart(contextData.cart, p);
            if (cartInfo.isInCart) {
                removeFromCart(p, prefix);
                return;
            }
        }

        let bodyData = null;

        if (prefix === 'parkings') {
            bodyData = { parkingDTO: p };
        } else if (prefix === 'fast-tracks') {
            if(p?.terminal?.airport?.code==contextData?.formValues?.origin_airport||p?.airportCode==contextData?.formValues?.origin_airport){
                bodyData = {
                    fastTrackDirectionFrom: p
                };
            } else{
                bodyData = {
                    fastTrackDirectionTo: p
                };
            }
        } else if (prefix === 'lounges') {

            if(p?.terminal?.airport?.code==contextData?.formValues?.origin_airport){
                bodyData = {
                    loungeDirectionFrom: p
                };
            } else{
                bodyData = {
                    loungeDirectionTo: p
                };
            }
            
        } else if (prefix === 'transfers') {

            let sideDtoType = 'departureSideDepartureTransfer';

            if(p?.fromAirport){
                if(p?.airportCode==contextData?.formValues?.origin_airport){
                    sideDtoType = 'departureSideArrivalTransfer';
                } else{
                    sideDtoType = 'arrivalSideDepartureTransfer'
                }
            } else{
                if(p?.airportCode==contextData?.formValues?.destination_airport){
                    sideDtoType = 'arrivalSideArrivalTransfer'
                }
            }
            
            
            bodyData = { [sideDtoType]: p };
        }

        axios.post(`/api/custom/${prefix}/add-to-cart?currency=${contextData.currency}`, {
            ...bodyData,
            searchId: contextData?.responseId
        }).then(r => {
            setSnack({
                message: i18n.t('cart.addedToCart'),
                severity: 'success',
                open: true
            })
            setCart(r?.data);
        }).catch(e => {
            setSnack({
                message: i18n.t('cart.addToCartError'),
                severity: 'error',
                open: true
            })
        })
    }

    const setFormValues = (e, fieldName = '') => {
        if (!e?.target) {
            window.localStorage.setItem('formValues', JSON.stringify({ ...contextData.formValues, [fieldName]: e }));
            setContextData((contextData) => ({ ...contextData, formValues: { ...contextData.formValues, [fieldName]: e } }))
         } else {
            window.localStorage.setItem('formValues', JSON.stringify({ ...contextData.formValues, [e.target.name]: e.target.value }));
            setContextData((contextData) => ({ ...contextData, formValues: { ...contextData.formValues, [e.target.name]: e.target.value } }))
            if(e?.target?.name === 'origin_airport' || e?.target?.name === 'destination_airport'){
                if(e?.target?.value === 'AUH'){
                    setCurrency('AED');
                 }
             }
         }

     }

    const setMultipleFormValues = (fields) => {
        const updatedFormValues = { ...contextData.formValues, ...fields };
        window.localStorage.setItem('formValues', JSON.stringify(updatedFormValues));
        setContextData((contextData) => ({ ...contextData, formValues: updatedFormValues }))
    }

    const setCurrency = (c) => {
        window.localStorage.setItem('currency', c);
        setContextData((contextData) => ({ ...contextData, currency: c }))
    }

    const resetCart = (type = '') => {

        if (type !== 'withoutFormValues') {
            window.localStorage.removeItem('formValues');
        }
        window.localStorage.removeItem('details');
        window.localStorage.removeItem('responseId');
        window.localStorage.removeItem('orderId');
        window.localStorage.removeItem('cart');
        window.localStorage.removeItem('transfer');
        window.localStorage.removeItem('lounge');
        window.localStorage.removeItem('parking');
        window.localStorage.removeItem('fastTrack');

        window.localStorage.setItem('successCartData', JSON.stringify(contextData.cart));

        setContextData((contextData) => ({
            ...contextData,
            formValues: type === 'withoutFormValues' ? contextData.formValues : defaultFormValues(),
            details: defaultDetails(),
            cart: null,
            transfer: null,
            lounge: null,
            parking: null,
            fastTrack: null,
            responseId: 0,
            orderId: 0
        }))

    }

    // Manuális localStorage tisztítás funkció hibakereséshez
    const clearAllLocalStorageData = () => {
        clearInvalidLocalStorage();
        window.localStorage.setItem('localStorageVersion', LOCALSTORAGE_VERSION);
        setSnack({
            message: i18n.t('messages.localStorageCleared'),
            severity: 'warning',
            open: true
        });
        // Kicsi késés után újratölti az oldalt
        setTimeout(() => window.location.reload(), 2000);
    }

    const setResponseId = id => {
        window.localStorage.setItem('responseId', id);
        setContextData((contextData) => ({ ...contextData, user: contextData.user, authKey: contextData.authKey, responseId: id }))
    };

    const setOrderId = id => {
        window.localStorage.setItem('orderId', id);
        setContextData((contextData) => ({ ...contextData, orderId: id }))
    };

    const setCart = cart => {
        window.localStorage.setItem('cart', JSON.stringify(cart));
        setContextData((contextData) => ({ ...contextData, cart: cart }))
    };

    const setTransfer = transfer => {
        window.localStorage.setItem('transfer', JSON.stringify(transfer));
        setContextData((contextData) => ({ ...contextData, transfer: transfer }))
    };

    const setLounge = lounge => {
        window.localStorage.setItem('lounge', JSON.stringify(lounge));
        setContextData((contextData) => ({ ...contextData, lounge: lounge }))
    };

    const setParking = parking => {
        window.localStorage.setItem('parking', JSON.stringify(parking));
        setContextData((contextData) => ({ ...contextData, parking: parking }))
    };

    const setFastTrack = fastTrack => {
        window.localStorage.setItem('fastTrack', JSON.stringify(fastTrack));
        setContextData((contextData) => ({ ...contextData, fastTrack: fastTrack }))
    };

    const setDetails = (details, cartId) => {
        if (cartId) {
            window.localStorage.setItem('details', JSON.stringify(details));
            setContextData((contextData) => ({ ...contextData, details: details }))
            console.log('setDetails', details)
            axios.patch(`/api/shopping-carts/${cartId}`, {
                id: cartId,
                surname: details?.surname,
                firstName: details?.firstname,
                email: details?.email,
                phone: details?.phone,
                directionFrom: {
                    ...contextData.cart.directionFrom,
                    flightNumber: details?.flightNumber,
                    flightArrival: details?.flightArrival ? new Date(details?.flightArrival).toISOString() : null,
                    flightDeparture: details?.flightDeparture ? new Date(details?.flightDeparture).toISOString() : null,    
                },
                directionTo: {
                    ...contextData.cart.directionTo,
                    flightNumber: details?.flightNumber2,
                    flightArrival: details?.flightArrival2 ? new Date(details?.flightArrival2).toISOString() : null,
                    flightDeparture: details?.flightDeparture2 ? new Date(details?.flightDeparture2).toISOString() : null,    
                }
            })
        }
    };


    const activateCart = (cartId) => {

        axios.get(`/api/custom/shopping-carts/${cartId}?currency=${contextData.currency}`)
            .then(resp => {

                axios.get(`/api/searches?shoppingCartId.equals=${cartId}`)
                    .then(res => {

                        // setResponseId
                        if (res.data && res.data[0] && res.data[0]?.id) {
                            const details = resp.data;

                            window.localStorage.setItem('cart', JSON.stringify(details?.order));
                            window.localStorage.setItem('orderId', details?.order?.id);
                            window.localStorage.setItem('details', JSON.stringify(details));
                            window.localStorage.setItem('responseId', res.data[0]?.id);

                            const formValues = {
                                origin_airport: res.data&&res.data[0]?res.data[0]?.departure?.code:'',
                                departureSideStartDate: res.data[0]?.departureSideStartDate?getLocalString(res.data[0]?.departureSideStartDate):'',
                                arrivalSideEndDate: res.data[0]?.arrivalSideEndDate?getLocalString(res.data[0]?.arrivalSideEndDate):'',
                                arrivalSideStartDate: res.data[0]?.arrivalSideStartDate?getLocalString(res.data[0]?.arrivalSideStartDate):'',
                                departureSideEndDate: res.data[0]?.departureSideEndDate?getLocalString(res.data[0]?.departureSideEndDate):'',
                                singleTrip: res.data[0]?.singleTrip,
                                destination_airport: res.data&&res.data[0]?res.data[0]?.arrival?.code:'',
                                numberOfAdults: res.data[0]?.numberOfAdults,
                                numberOfChildren: res.data[0]?.numberOfChildren,
                                numberOfInfants: res.data[0]?.numberOfInfants
                            };
                            
                            window.localStorage.setItem('formValues', JSON.stringify(formValues));


                            setContextData((contextData) => ({
                                ...contextData,
                                details: {
                                    surname: details?.surname,
                                    firstname: details?.firstName,
                                    email: details?.email,
                                    phone: details?.phone,
                                    flightNumber: details?.flightNumber,
                                    flightArrival: details?.flightArrival,
                                    flightDeparture: details?.flightDeparture,
                                    ffp: details?.ffpNumber
                                },
                                orderId: details?.order?.id,
                                cart: details,
                                responseId: res.data[0]?.id,
                                formValues: formValues
                            }))


                            axios.get(`/api/custom/lounges?searchId=${parseInt(res.data[0]?.id, 10)}&currency=${contextData.currency}`).then(r => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    lounge: r?.data
                                }))
                            }).catch(e => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    lounge: null
                                }))
                            })

                            axios.get(`/api/custom/transfers?searchId=${parseInt(res.data[0]?.id, 10)}&currency=${contextData.currency}`).then(r => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    transfer: r?.data
                                }))
                            }).catch(e => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    transfer: null
                                }))
                            })

                            axios.get(`/api/custom/parkings?searchId=${parseInt(res.data[0]?.id, 10)}&currency=${contextData.currency}`).then(r => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    parking: r?.data
                                }))
                            }).catch(e => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    parking: null
                                }))
                            })

                            axios.get(`/api/custom/fast-tracks?searchId=${parseInt(res.data[0]?.id, 10)}&currency=${contextData.currency}`).then(r => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    fastTrack: r?.data
                                }))
                            }).catch(e => {
                                setContextData((contextData) => ({
                                    ...contextData,
                                    fastTrack: null
                                }))
                            })

                        }

                    })

            })
    }


    const setLanguage = lang => {
        props.i18n.changeLanguage(lang)
    }


    return (
          <GlobalContext.Provider
             value={{
                 contextData: contextData,
                 setFormValues: setFormValues,
                 setMultipleFormValues: setMultipleFormValues,
                setResponseId: setResponseId,
                setCart: setCart,
                setDetails: setDetails,
                setParking: setParking,
                setFastTrack: setFastTrack,
                setLounge: setLounge,
                setTransfer: setTransfer,
                login: login,
                resetPassword: resetPassword,
                resetPasswordFinish: resetPasswordFinish,
                register: register,
                logout: logout,
                isLoggedIn: isLoggedIn,
                setOrderId: setOrderId,
                resetCart: resetCart,
                clearAllLocalStorageData: clearAllLocalStorageData,
                addToCart: addToCart,
                removeFromCart: removeFromCart,
                setSnack: setSnack,
                activateCart: activateCart,
                setCurrency: setCurrency,
                deleteMyProfile: deleteMyProfile,
                setLanguage: setLanguage,
                activate: activate,
                resetActivate: resetActivate
            }}
        >
            <Snackbar
                open={snack.open}
                autoHideDuration={6000}
                onClose={() => setSnack(defaultSnack)}

            >
                <Alert onClose={() => setSnack(defaultSnack)} severity={snack.severity} sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
            {props.children}
        </GlobalContext.Provider>
    );
};

export default withTranslation()(GlobalState);;
